import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';

// Layouts
import AppLayout from './components/layout/AppLayout';
import Spinner from './components/ui/Spinner';

// Route Definitions & Auth
import { ROUTES } from './constants/routes';
import { useAuth } from './hooks/useAuth';

// Public Pages
import LandingPage from './pages/public/LandingPage';
import VolunteerCareersPage from './pages/public/VolunteerCareersPage';
import FundraisingPage from './pages/public/FundraisingPage';
import BlogPage from './pages/public/BlogPage';
import SupportHelpPage from './pages/public/SupportHelpPage';
import ReferralRedirectPage from './pages/public/ReferralRedirectPage';
import HostStudioPage from './pages/public/HostStudioPage';

// Live Game Pages (Web & iOS PWA Experience)
import PlayGamesPage from './pages/games/PlayGamesPage';
import SeasonLobbyPage from './pages/games/SeasonLobbyPage';
import QuizLobbyPage from './pages/games/QuizLobbyPage';
import GamePlayerPage from './pages/games/GamePlayerPage';
import GameResultsPage from './pages/games/GameResultsPage';
import GameLeaderboardPage from './pages/games/GameLeaderboardPage';
import SeasonLeaderboardPage from './pages/games/SeasonLeaderboardPage';
import QuizLeaderboardPage from './pages/games/QuizLeaderboardPage';
import GameAnswersReviewPage from './pages/games/GameAnswersReviewPage';

// Auth Page
import LoginPage from './pages/auth/LoginPage';

// Legal Pages
import UserDeletionRequestPage from './pages/legal/UserDeletionRequestPage';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsAndConditionsPage from './pages/legal/TermsAndConditionsPage';

// User Profile & Contestant Page
import UserProfilePage from './pages/user/UserProfilePage';

// Host Studio & Season Management Pages (Lazy Loaded for Performance)
const SeasonDashboardPage = lazy(() => import('./pages/studio/SeasonDashboardPage'));
const CreateSeasonForm = lazy(() => import('./pages/studio/SeasonFormPage').then(m => ({ default: m.CreateSeason })));
const UpdateSeasonForm = lazy(() => import('./pages/studio/SeasonFormPage').then(m => ({ default: m.UpdateSeason })));
const CreateQuizForm = lazy(() => import('./pages/studio/QuizFormPage').then(m => ({ default: m.CreateQuiz })));
const UpdateQuizForm = lazy(() => import('./pages/studio/QuizFormPage').then(m => ({ default: m.UpdateQuiz })));
const CreateQuestionForm = lazy(() => import('./pages/studio/QuestionFormPage').then(m => ({ default: m.CreateQuestion })));
const UpdateQuestionForm = lazy(() => import('./pages/studio/QuestionFormPage').then(m => ({ default: m.UpdateQuestion })));
const ManageQuestionsPage = lazy(() => import('./pages/studio/ManageQuestionsPage'));
const QuizResultsPage = lazy(() => import('./pages/studio/QuizResultsPage'));

const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <Spinner />
  </div>
);

/**
 * Route guard: Redirects unauthenticated visitors to About Us (Landing page '/')
 */
const RequireAuth = ({ children }) => {
  const { user } = useAuth();
  if (!user) {
    return <Navigate to={ROUTES.PUBLIC.HOME} replace />;
  }
  return children ? children : <Outlet />;
};

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public standalone legal pages (no navbar/footer) */}
          <Route path={ROUTES.LEGAL.DELETION_REQUEST} element={<UserDeletionRequestPage />} />
          <Route path={ROUTES.LEGAL.PRIVACY} element={<PrivacyPolicyPage />} />
          <Route path={ROUTES.LEGAL.TERMS} element={<TermsAndConditionsPage />} />

          {/* Full Screen Live Game Screen (Protected: unauthenticated redirects to About Us) */}
          <Route
            path="/play/season/:seasonId/quiz/:quizId/play"
            element={
              <RequireAuth>
                <GamePlayerPage />
              </RequireAuth>
            }
          />

          {/* App Layout with Top Navigation & Drawer */}
          <Route path="/" element={<AppLayout />}>
            {/* Public Community & Info Pages */}
            <Route index element={<LandingPage />} />
            <Route path="about" element={<Navigate to="/" replace />} />
            <Route path="about-us" element={<Navigate to="/" replace />} />
            <Route path="volunteer" element={<VolunteerCareersPage />} />
            <Route path="join-us" element={<VolunteerCareersPage />} />
            <Route path="careers" element={<VolunteerCareersPage />} />
            <Route path="donate" element={<FundraisingPage />} />
            <Route path="fundraising" element={<FundraisingPage />} />
            <Route path="blog" element={<BlogPage />} />
            <Route path="blog/:slug" element={<BlogPage />} />
            <Route path="support" element={<SupportHelpPage />} />
            <Route path="r/:code" element={<ReferralRedirectPage />} />
            <Route path="join" element={<ReferralRedirectPage />} />
            <Route path="login" element={<LoginPage />} />
            {/* 👤 Public Contestant Profile */}
            <Route path="profile/:userId" element={<UserProfilePage />} />

            {/* 🔒 Protected Routes: Play Games, Ranking, and Host Studio (redirects to About Us if not logged in) */}
            <Route element={<RequireAuth />}>
              {/* 👤 Personal Profile & Contestant Account */}
              <Route path="profile" element={<UserProfilePage />} />
              <Route path="account" element={<Navigate to="/profile" replace />} />
              <Route path="my-profile" element={<Navigate to="/profile" replace />} />

              {/* 🎮 Play Games & Live Tournaments */}
              <Route path="play" element={<PlayGamesPage />} />
              <Route path="games" element={<PlayGamesPage />} />
              <Route path="play/season/:seasonId" element={<SeasonLobbyPage />} />
              <Route path="play/season/:seasonId/quiz/:quizId" element={<QuizLobbyPage />} />
              <Route path="play/season/:seasonId/quiz/:quizId/results" element={<GameResultsPage />} />
              <Route path="play/season/:seasonId/leaderboard" element={<SeasonLeaderboardPage />} />
              <Route path="play/season/:seasonId/quiz/:quizId/leaderboard" element={<QuizLeaderboardPage />} />
              <Route path="play/season/:seasonId/quiz/:quizId/answers" element={<GameAnswersReviewPage />} />

              {/* 🏆 Ranking / Global Leaderboard */}
              <Route path="leaderboard" element={<GameLeaderboardPage />} />
              <Route path="ranking" element={<Navigate to="/leaderboard" replace />} />

              {/* ⛪ Host Studio & Season Management for Community Hosts */}
              <Route path="manage-quizzes" element={<HostStudioPage />} />
              <Route path="manage-quizzes/season/dashboard" element={<SeasonDashboardPage />} />
              <Route path="manage-quizzes/season/create" element={<CreateSeasonForm />} />
              <Route path="manage-quizzes/season/update" element={<UpdateSeasonForm />} />
              <Route path="manage-quizzes/quizzes/create" element={<CreateQuizForm />} />
              <Route path="manage-quizzes/quizzes/update" element={<UpdateQuizForm />} />
              <Route path="manage-quizzes/questions/create" element={<CreateQuestionForm />} />
              <Route path="manage-quizzes/questions/update" element={<UpdateQuestionForm />} />
              <Route path="manage-quizzes/questions" element={<ManageQuestionsPage />} />
              <Route path="manage-quizzes/quiz-results" element={<QuizResultsPage />} />

              {/* Friendly Aliases */}
              <Route path="studio" element={<Navigate to="/manage-quizzes" replace />} />
              <Route path="create-season" element={<Navigate to="/manage-quizzes/season/create" replace />} />
              <Route path="update-season" element={<Navigate to="/manage-quizzes/season/update" replace />} />
              <Route path="create-quiz" element={<Navigate to="/manage-quizzes/quizzes/create" replace />} />
              <Route path="update-quiz" element={<Navigate to="/manage-quizzes/quizzes/update" replace />} />
              <Route path="create-question" element={<Navigate to="/manage-quizzes/questions/create" replace />} />
              <Route path="update-question" element={<Navigate to="/manage-quizzes/questions/update" replace />} />
              <Route path="season-dashboard" element={<Navigate to="/manage-quizzes/season/dashboard" replace />} />
              <Route path="quiz-results" element={<Navigate to="/manage-quizzes/quiz-results" replace />} />
              <Route path="seasons" element={<Navigate to="/manage-quizzes" replace />} />
              <Route path="quizzes" element={<Navigate to="/manage-quizzes" replace />} />
            </Route>

            {/* Other Aliases & Catch-all */}
            <Route path="home" element={<Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
